import { FormPageLayout, LoadingDots } from '@expressable/syntax-ds';
import { EvaluationDuration } from 'domains/calendars/types';
import { SignupPath, useSignupRoutingRules } from 'domains/signupDirect/businessLogic/hooks';
import { useSignupDirectCtx } from 'domains/signupDirect/context';
import { DIRECT_SIGNUP_MIXPANEL_EVENTS } from 'domains/signupDirect/misc/mixpanelTracking';
import { useMinimumAvailability } from 'domains/signupDirect/misc/useMinimumAvailability';
import { useSignupEvaluationCalendar } from 'domains/signupDirect/misc/useSignupEvaluationCalendar';
import { useSignupNavigationGuard } from 'domains/signupDirect/misc/useSignupNavigationGuard';
import { get, isObject } from 'lodash';
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBoolean } from 'react-use';
import { routes } from 'router/routes';
import { useTrack } from 'utils/hooks/useMixPanel';
import { useLDFlags } from 'utils/launchdarkly/launchdarkly';
import { ExperimentLandingPage } from './ExperimentLandingPage';

export const SignupScheduleLandingPage: React.FC = () => {
  const { isLoading: isLoadingRedirects, signupPath } = useForcedRedirects();
  useSignupNavigationGuard();

  const { showLandingPage } = useLandingPageExperiment(!isLoadingRedirects);

  if (isLoadingRedirects || signupPath === SignupPath.CONSULTATION || !showLandingPage) {
    return (
      <FormPageLayout variant="full">
        <LoadingDots className="flex flex-col h-max static" />
      </FormPageLayout>
    );
  }

  return <ExperimentLandingPage variant={signupPath} />;
};

/** Handles landing page experiment forking logic */
const useLandingPageExperiment = (isEnabled: boolean) => {
  /** NOTE(juan): intentionally do not read the specific flag needed until its 100% needed, this is to avoid triggering the experiment on cases where it doesn't apply */
  const LDFlags = useLDFlags();

  const navigate = useNavigate();

  const { track } = useTrack();

  const [showLandingPage, setShowLandingPage] = useBoolean(false);

  const { signupPath } = useSignupRoutingRules();

  useEffect(() => {
    if (!isEnabled) return;
    // Splits NOT allowed -> Single evaluation
    if (signupPath === SignupPath.SINGLE_EVALUATION) {
      if (LDFlags.signupShowLandingPage) {
        setShowLandingPage(true);
      } else {
        track(DIRECT_SIGNUP_MIXPANEL_EVENTS.STEPS.LANDING_PAGE.EXPERIMENT.NO_LANDING_SINGLES);
        navigate(routes.signupDirect.evaluation.schedule.single, { replace: true });
      }
      return;
    }

    // Splits allowed
    if (signupPath === SignupPath.SPLIT_EVALUATIONS) {
      if (LDFlags.signupShowLandingPage) {
        setShowLandingPage(true);
      } else {
        track(DIRECT_SIGNUP_MIXPANEL_EVENTS.STEPS.LANDING_PAGE.EXPERIMENT.NO_LANDING_SPLITS);
        navigate(routes.signupDirect.evaluation.schedule.split.first, { replace: true });
      }
      return;
    }

    throw new Error('Reached end of forced redirects');
  }, [isEnabled, signupPath]);

  return { showLandingPage };
};

// Handles forced redirect in case a choice isn't available to the user
const useForcedRedirects = () => {
  const [isHandlingRedirects, setIsHandlingRedirects] = useBoolean(true);
  const { filters, isResumedLead, onContinueToJotformRegistrationForm, resumedLeadState } = useSignupDirectCtx();
  if (!filters) throw new Error('Expected filters to be set');

  const navigate = useNavigate();

  const { track } = useTrack();

  const { isEvalAllowed, isSplitEvaluationAllowed, signupPath } = useSignupRoutingRules();

  const { isLoading: isLoadingSplits, evaluationCalendarQuery: splitsEvaluationCalendarQuery } =
    useSignupEvaluationCalendar(filters, EvaluationDuration.Split);
  const { isAvailabilityOverMinimum: areSplitsAvailable } = useMinimumAvailability({
    evaluationCalendarQuery: splitsEvaluationCalendarQuery,
    disabled: true,
  });

  const { isLoading: isLoadingSingle, evaluationCalendarQuery: singleEvaluationCalendarQuery } =
    useSignupEvaluationCalendar(filters, EvaluationDuration.Single);
  const { isAvailabilityOverMinimum: areSinglesAvailable } = useMinimumAvailability({
    evaluationCalendarQuery: singleEvaluationCalendarQuery,
    disabled: true,
  });

  // Evaluation schedule allowed or not
  useEffect(() => {
    track(DIRECT_SIGNUP_MIXPANEL_EVENTS.STEPS.LANDING_PAGE.BASE, {
      stateCode: filters.stateCode,
      therapyTarget: filters.therapyTarget,
      paymentMethodId: filters.paymentMethod.id,
      insuranceName: get(filters, 'paymentMethod.insuranceData.displayName'),
      isInsurance: filters.paymentMethod.type !== 'privatePay',
      hasPrefilledAppointment: isObject(resumedLeadState?.prefilledAppointment),
      language: filters.language,
      clinicalConcerns: filters.clinicalConcernKeys,
      ClinicalAreas: filters.clinicalAreas,
      isResumedLead,
    });

    // Prefilled appointment
    if (resumedLeadState?.prefilledAppointment) {
      track(DIRECT_SIGNUP_MIXPANEL_EVENTS.STEPS.EVAL.SCHEDULE.FORCED_REDIRECTS.PREFILLED_APPOINTMENT);
      navigate(routes.signupDirect.evaluation.details, { replace: true });
      return;
    }

    // Evaluation NOT allowed
    if (!isEvalAllowed.allowed) {
      track(
        DIRECT_SIGNUP_MIXPANEL_EVENTS.STEPS.EVAL.EXCLUSION,
        {
          isResumedLead,
          reason: isEvalAllowed.reason,
          step: DIRECT_SIGNUP_MIXPANEL_EVENTS.STEPS.LANDING_PAGE,
        },
        {
          send_immediately: true,
        },
      );

      onContinueToJotformRegistrationForm();
      return;
    }

    // Splits NOT allowed -> Single evaluation
    if (!isSplitEvaluationAllowed.allowed) {
      track(DIRECT_SIGNUP_MIXPANEL_EVENTS.STEPS.EVAL.SCHEDULE.FORCED_REDIRECTS.SPLITS_NOT_ALLOWED, {
        isResumedLead,
        reason: isSplitEvaluationAllowed.reason,
        step: DIRECT_SIGNUP_MIXPANEL_EVENTS.STEPS.LANDING_PAGE,
      });
    }

    setIsHandlingRedirects(false);
  }, [isEvalAllowed, isSplitEvaluationAllowed]);

  useEffect(() => {
    if (isHandlingRedirects) return;
    if (isLoadingSingle || isLoadingSplits) return;

    // Availability checks
    if (signupPath === SignupPath.SINGLE_EVALUATION) {
      if (!areSinglesAvailable) {
        track(DIRECT_SIGNUP_MIXPANEL_EVENTS.STEPS.EVAL.SCHEDULE.FORCED_REDIRECTS.SINGLES_UNAVAILABLE);
        onContinueToJotformRegistrationForm();
        return;
      }
    }

    if (signupPath === SignupPath.SPLIT_EVALUATIONS) {
      if (!areSplitsAvailable) {
        track(DIRECT_SIGNUP_MIXPANEL_EVENTS.STEPS.EVAL.SCHEDULE.FORCED_REDIRECTS.SPLITS_UNAVAILABLE);
        onContinueToJotformRegistrationForm();
        return;
      }
    }
  }, [areSplitsAvailable, areSinglesAvailable, isHandlingRedirects, isLoadingSingle, isLoadingSplits]);

  return { isLoading: isHandlingRedirects || isLoadingSingle || isLoadingSplits, signupPath };
};
